import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const accent = product.accent || "#D4AF37";

  const onAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    add(product, 1);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.06, ease: [0.32, 0.72, 0, 1] }}
      className="group"
      data-testid="product-card"
      style={{ "--accent": accent }}
    >
      <Link to={`/product/${product.slug}`} className="block">
        {/* Hover-flip image */}
        <div
          className="relative aspect-[4/5] mb-5 hairline border overflow-hidden bg-ink-900 product-flip"
          data-testid={`product-image-${product.id}`}
        >
          {/* Accent halo */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at center, ${accent}22 0%, transparent 60%)`,
            }}
          ></div>

          {/* Front image */}
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out group-hover:opacity-0"
          />
          {/* Back image (revealed on hover) */}
          {product.image_back && (
            <img
              src={product.image_back}
              alt={`${product.name} alternate view`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100 scale-105 group-hover:scale-100 transform transition-transform"
            />
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-ink-950/60 via-transparent to-transparent pointer-events-none"></div>

          <div className="absolute top-4 left-4 label-eyebrow bg-ink-950/70 px-3 py-1.5 border border-white/10">
            {product.category_name}
          </div>

          {/* Variant accent badge */}
          {product.sub_tagline && (
            <div
              className="absolute bottom-4 left-4 right-4 flex items-center gap-2 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500"
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: accent, boxShadow: `0 0 12px ${accent}` }}
              ></span>
              <span
                className="text-[10px] tracking-[0.28em] uppercase font-medium"
                style={{ color: accent }}
              >
                {product.sub_tagline}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="font-serif text-2xl text-bone leading-tight group-hover:text-gold transition-colors">
            {product.name}
          </h3>
          <p className="text-mist text-sm font-light italic">{product.tagline}</p>
          <p className="text-bone/80 text-sm font-light leading-relaxed">
            {product.key_benefit}
          </p>
          <div className="flex items-center justify-between pt-3">
            <div className="text-xs text-mist tracking-wider">
              {product.size} · {product.quantity} pcs
            </div>
            <div className="flex items-baseline gap-2">
              {product.mrp && product.mrp > product.price && (
                <span className="num-display text-sm text-mist/50 line-through" data-testid="product-mrp">
                  ₹{product.mrp}
                </span>
              )}
              <span className="num-display text-xl text-gold">₹{product.price}</span>
              {product.mrp && product.mrp > product.price && (
                <span
                  className="num-display text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-400/30 px-1.5 py-0.5 tracking-wider"
                  data-testid="product-discount"
                >
                  {Math.round((1 - product.price / product.mrp) * 100)}% OFF
                </span>
              )}
            </div>
          </div>
          <p
            className="flex items-center gap-1.5 text-[10px] text-mist/70 font-light tracking-wider pt-1"
            data-testid="dispatch-badge"
          >
            <span className="inline-block w-1 h-1 rounded-full bg-gold/60"></span>
            Dispatched from Dwarka, New Delhi
          </p>
        </div>
      </Link>

      <button
        onClick={onAdd}
        className="btn-ghost w-full mt-5"
        data-testid={`add-to-cart-btn-${product.id}`}
      >
        Add to cart
      </button>
    </motion.article>
  );
}
